import apiClient from 'src/services/apiClient'
import {SECONDARY_ASSUMPTION_STATES} from 'src/constants/secondaryAssumptions'

// Closes the web push notifications that no longer lead anywhere (therapy-service
// specs/2026-10-notification-push.md, RF13). The same reminder reaches every device of the user:
// once the dose is answered on one of them, the notification still on screen on the others (it
// stays until touched) or delivered late when a browser reopens is closed the next time the
// portal is opened there.
//
// - reminder of a dose (data.secondaryAssumptionId, set by public/service-worker.js): closed when
//   the dose is no longer PLANNED, or no longer exists (404)
// - generic notification (tag 'web-push'): closed when GET /web-push/latest has nothing to open (404)
// - anything else (activity changes, tag 'activity-...'): never touched
// Any other error, or no answer within CHECK_TIMEOUT_MS, leaves the notification as it is.

export const GENERIC_TAG = 'web-push'
const CHECK_TIMEOUT_MS = 8000

export type CleanupApi = {
  getSecondaryAssumption: (secondaryAssumptionId: string) => Promise<{state?: string} | undefined>
  getLatestWebPush: () => Promise<unknown>
}

const defaultApi: CleanupApi = {
  getSecondaryAssumption: id => apiClient.getSecondaryAssumption(id, undefined),
  getLatestWebPush: () => apiClient.getLatestWebPush(),
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms)
    promise.then(
      value => { clearTimeout(timer); resolve(value) },
      error => { clearTimeout(timer); reject(error) },
    )
  })
}

function isNotFound(error: any): boolean {
  return error?.response?.status === 404
}

// Whether the notification is handled and can be closed. Never rejects.
export async function isHandled(notification: Notification, api: CleanupApi = defaultApi, timeoutMs = CHECK_TIMEOUT_MS): Promise<boolean> {
  const secondaryAssumptionId = notification.data?.secondaryAssumptionId
  try {
    if (typeof secondaryAssumptionId === 'string' && secondaryAssumptionId) {
      const assumption = await withTimeout(api.getSecondaryAssumption(secondaryAssumptionId), timeoutMs)
      return Boolean(assumption?.state) && assumption?.state !== SECONDARY_ASSUMPTION_STATES.PLANNED
    }
    if (notification.tag === GENERIC_TAG) {
      await withTimeout(api.getLatestWebPush(), timeoutMs)
      return false
    }
    return false
  } catch (error) {
    return isNotFound(error)
  }
}

// Checks every notification of the portal on screen (one call each, usually none or one) and
// closes the handled ones. Resolves with how many were closed.
export async function closeHandledNotifications(registration: ServiceWorkerRegistration, api: CleanupApi = defaultApi, timeoutMs = CHECK_TIMEOUT_MS): Promise<number> {
  if (typeof registration?.getNotifications !== 'function') return 0
  const notifications = await registration.getNotifications()
  const handled = await Promise.all(notifications.map(notification => isHandled(notification, api, timeoutMs)))
  let closed = 0
  notifications.forEach((notification, index) => {
    if (handled[index]) {
      notification.close()
      closed += 1
    }
  })
  return closed
}

let running: Promise<void> | undefined

// Entry point, called when the session is ready and whenever the portal comes back to the
// foreground (src/services/push/useReminderPush.ts). Does nothing where the service worker or
// getNotifications() are not available (e.g. development builds, which register no worker), and
// never runs twice at once (focus and visibilitychange usually fire together).
export function cleanUpHandledNotifications(api: CleanupApi = defaultApi): Promise<void> {
  if (running) return running
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !navigator.serviceWorker) {
    return Promise.resolve()
  }
  running = withTimeout(navigator.serviceWorker.ready, CHECK_TIMEOUT_MS)
    .then(registration => closeHandledNotifications(registration, api))
    .then(() => undefined, () => undefined)
    .finally(() => { running = undefined })
  return running
}
